import Icon from './Icon';

/**
 * The transport — the jukebox's own family of glyphs (SPEC-014 PR-14.3).
 *
 * A SECOND FILE rather than six more exports in `ControlIcons.js`, and
 * `ControlIcons.js` predicted exactly this: "when a second family arrives it
 * gets its own file". These six are a family — they are the buttons of a
 * PLAYER, they are only ever drawn together, and every one of them means
 * something no other glyph in the product means.
 *
 * Drawn in the shared `Icon` frame like everything else, so the square caps and
 * mitred joins of §10 come for free. The one thing this set does that the rest
 * of the product does not is FILL: a play triangle and a pause bar are solid
 * shapes everywhere anybody has ever seen them, and an outlined play triangle at
 * 20px reads as a chevron pointing the wrong way. So they carry an explicit
 * `fill="currentColor"` and no stroke, which keeps a caller's single `color`
 * still tinting the whole control.
 */

/**
 * The level meter, which is this product's word for "music".
 *
 * Four bars at four heights rather than a note, and the choice is §10's rather
 * than a whim: the icon language here is chrome and steel with hard edges, and a
 * note is a curve, a stem and a flag — three rounded gestures that come out of
 * this frame's square caps looking broken. A meter is four rectangles.
 *
 * It also has somewhere to go. The header's button animates these bars while a
 * track is playing (`jukebox.css`), so the control says whether the jukebox is
 * on WITHOUT a second badge, a dot or a colour — and says it in the one visual
 * language that could not mean anything else.
 */
export function EqualiserIcon(props) {
    return (
        <Icon {...props} fill="currentColor" stroke="none">
            <rect className="equaliser__bar equaliser__bar--1" x="3.5" y="9" width="3" height="9" />
            <rect className="equaliser__bar equaliser__bar--2" x="9" y="5" width="3" height="13" />
            <rect className="equaliser__bar equaliser__bar--3" x="14.5" y="11" width="3" height="7" />
            <rect className="equaliser__bar equaliser__bar--4" x="20" y="7" width="3" height="11" />
        </Icon>
    );
}

export function PlayIcon(props) {
    return (
        <Icon {...props} fill="currentColor" stroke="none">
            <path d="M7 4.5 20 12 7 19.5z" />
        </Icon>
    );
}

export function PauseIcon(props) {
    return (
        <Icon {...props} fill="currentColor" stroke="none">
            <rect x="6" y="4.5" width="4" height="15" />
            <rect x="14" y="4.5" width="4" height="15" />
        </Icon>
    );
}

/**
 * The step back, and the step on.
 *
 * A triangle against a bar, mirrored — the shape every player has used since
 * the cassette deck this product's era ran on. DRAWN twice rather than one glyph
 * flipped in CSS, for the reason `ForwardIcon` gives about the deck's chevrons:
 * a transform is a second way of saying what a `d` attribute already says, and
 * one a right-to-left stylesheet would then have to know not to flip again.
 */
export function SkipBackIcon(props) {
    return (
        <Icon {...props} fill="currentColor" stroke="none">
            <rect x="5" y="5" width="3" height="14" />
            <path d="M19 5v14L9.5 12z" />
        </Icon>
    );
}

export function SkipForwardIcon(props) {
    return (
        <Icon {...props} fill="currentColor" stroke="none">
            <path d="M5 5v14l9.5-7z" />
            <rect x="16" y="5" width="3" height="14" />
        </Icon>
    );
}

/**
 * Shuffle: two paths that cross, with an arrow on each.
 *
 * Stroked rather than filled, unlike the rest of this family, because it is the
 * only one of the six that is a diagram rather than a symbol — it says "these
 * two swap over", and a filled version would be a blob. The arrowheads are
 * mitred `L` shapes, not chevrons, so they match the frame's joins.
 */
export function ShuffleIcon(props) {
    return (
        <Icon {...props}>
            <path d="M3.5 6.5h3.8l9.4 11h3.8" />
            <path d="M3.5 17.5h3.8l9.4-11h3.8" />
            <path d="m17.5 3.8 3 2.7-3 2.7" />
            <path d="m17.5 14.8 3 2.7-3 2.7" />
        </Icon>
    );
}
